import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTOPLAY_MS = 2000;

const GallerySection = () => {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setItems(res.data || []);
      } catch (err) {
        console.error('Failed to fetch gallery:', err);
      }
    };
    fetch();
  }, []);

  const total = items.length;

  const next = useCallback(() => setActive((i) => (total ? (i + 1) % total : 0)), [total]);
  const prev = useCallback(() => setActive((i) => (total ? (i - 1 + total) % total : 0)), [total]);

  // Autoplay — always on
  useEffect(() => {
    if (total < 2) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [total, next]);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev]);

  // Touch swipe
  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  if (total === 0) return null;

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section-padding"
      style={{
        background: 'linear-gradient(180deg, #0f0a1e 0%, #1a0e2e 50%, #0f0a1e 100%)',
        overflow: 'hidden',
      }}
    >
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div className="hero-scroll gallery-scroll-cue" aria-hidden="true">
          <span className="hero-scroll-label">Scroll</span>
          <span className="hero-scroll-track">
            <span className="hero-scroll-dot" />
          </span>
        </div>
        <h2 style={{
          textAlign: 'center',
          fontSize: 'clamp(32px, 4vw, 48px)',
          fontWeight: 700,
          marginBottom: '12px',
        }}>
          <span style={{ color: '#00d4ff' }}>Gallery</span>
        </h2>
        <p style={{
          textAlign: 'center',
          fontSize: '15px',
          color: '#b0adc0',
          marginBottom: '12px',
        }}>
          Moments from the journey.
        </p>
        <div className="section-divider" style={{ marginBottom: '50px' }} />

        {/* 3D Stage */}
        <div
          className="gallery-3d-stage"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div className="gallery-3d-track">
            {items.map((item, idx) => {
              const offset = ((idx - active + total) % total);
              // normalize to -halfN .. +halfN
              const shifted = offset > total / 2 ? offset - total : offset;
              const abs = Math.abs(shifted);
              const isCenter = shifted === 0;
              const rotY = shifted * -22;   // tilt away from center
              const translateX = shifted * 220;   // spread
              const translateZ = -abs * 140;
              const scale = isCenter ? 1 : Math.max(0.7, 1 - abs * 0.12);
              const opacity = abs > 3 ? 0 : (isCenter ? 1 : 0.6 - abs * 0.08);
              const zIndex = 100 - abs;

              const clickable = !!item.link;
              const handleClick = () => {
                if (!isCenter) {
                  setActive(idx);
                } else if (clickable) {
                  window.open(item.link, '_blank', 'noopener,noreferrer');
                }
              };

              return (
                <div
                  key={item.gallery_id}
                  data-testid={`gallery-card-${idx}`}
                  className={`gallery-3d-card ${isCenter ? 'is-center' : ''}`}
                  style={{
                    transform: `translate(-50%, -50%) translateX(${translateX}px) translateZ(${translateZ}px) rotateY(${rotY}deg) scale(${scale})`,
                    opacity,
                    zIndex,
                    cursor: isCenter ? (clickable ? 'pointer' : 'default') : 'pointer',
                    pointerEvents: opacity === 0 ? 'none' : 'auto',
                  }}
                  onClick={handleClick}
                >
                  <img src={item.image} alt={item.caption || `Gallery ${idx + 1}`} draggable={false} />
                  <div className="gallery-3d-shine" />
                  {item.caption && isCenter && (
                    <div className="gallery-3d-caption">{item.caption}</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Nav arrows */}
          {total > 1 && (
            <>
              <button
                data-testid="gallery-prev-btn"
                className="gallery-3d-nav gallery-3d-nav-left"
                onClick={prev}
                aria-label="Previous"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                data-testid="gallery-next-btn"
                className="gallery-3d-nav gallery-3d-nav-right"
                onClick={next}
                aria-label="Next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
        </div>

        {/* Dot indicators */}
        {total > 1 && (
          <div className="gallery-3d-dots">
            {items.map((_, idx) => (
              <button
                key={idx}
                data-testid={`gallery-dot-${idx}`}
                onClick={() => setActive(idx)}
                className={`gallery-3d-dot ${idx === active ? 'active' : ''}`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default GallerySection;
